import axios from 'axios';
import { API_BASE } from '../config/api';

const SOURCE_LANG = 'fr';
const originalByNode = new WeakMap();

const sanitizeLang = (value) => String(value || 'fr').toLowerCase().split('-')[0];

const isTextCandidate = (text) => {
  const value = String(text || '').trim();
  if (!value) return false;
  if (value.length < 2 || value.length > 500) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(value)) return false;
  return true;
};

const shouldSkipNode = (node) => {
  const parent = node?.parentElement;
  if (!parent) return true;
  const tag = parent.tagName;
  if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CODE', 'PRE'].includes(tag)) return true;
  if (parent.closest('.notranslate, [translate="no"], [data-no-auto-translate="1"]')) return true;
  return false;
};

const collectCandidateNodes = (root) => {
  const out = [];
  if (!root) return out;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    if (!shouldSkipNode(current) && isTextCandidate(current.nodeValue)) {
      out.push(current);
    }
    current = walker.nextNode();
  }
  return out;
};

const splitBatches = (items, size = 40) => {
  const batches = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
};

const restoreOriginalTexts = (root) => {
  const nodes = collectCandidateNodes(root);
  nodes.forEach((node) => {
    const original = originalByNode.get(node);
    if (original) node.nodeValue = original;
  });
};

const translateNodes = async (root, targetLang, cache) => {
  const nodes = collectCandidateNodes(root);
  if (!nodes.length) return;

  const originals = nodes.map((node) => {
    if (!originalByNode.has(node)) {
      originalByNode.set(node, String(node.nodeValue || ''));
    }
    return originalByNode.get(node) || '';
  });

  const uniques = [...new Set(originals.filter(isTextCandidate))];
  const toTranslate = uniques.filter((text) => !cache.has(text));
  if (toTranslate.length) {
    const batches = splitBatches(toTranslate, 50);
    for (const batch of batches) {
      try {
        const res = await axios.post(`${API_BASE}/api/translate`, {
          texts: batch,
          source_lang: SOURCE_LANG,
          target_lang: targetLang
        });
        const translated = Array.isArray(res.data?.translations) ? res.data.translations : [];
        batch.forEach((src, index) => {
          cache.set(src, translated[index] || src);
        });
      } catch (_) {
        batch.forEach((src) => cache.set(src, src));
      }
    }
  }

  nodes.forEach((node) => {
    const original = originalByNode.get(node);
    if (!original) return;
    const translated = cache.get(original);
    if (translated && translated !== node.nodeValue) node.nodeValue = translated;
  });
};

export const setupAutoTranslate = (targetLanguage) => {
  const targetLang = sanitizeLang(targetLanguage);
  const root = document.getElementById('root');
  if (!root) return () => {};

  if (targetLang === SOURCE_LANG) {
    restoreOriginalTexts(root);
    return () => {};
  }

  const cache = new Map();
  let disposed = false;
  let timer = null;

  const run = async () => {
    if (disposed) return;
    await translateNodes(root, targetLang, cache);
  };

  const schedule = () => {
    if (disposed) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      run();
    }, 220);
  };

  run();
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, characterData: true });

  return () => {
    disposed = true;
    clearTimeout(timer);
    observer.disconnect();
  };
};

